import { useAuiState, type ToolCallMessagePartComponent } from "@assistant-ui/react";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XCircleIcon,
} from "lucide-react";
import React, { memo, useMemo, useRef, type FC, type PropsWithChildren, useState } from "react";
import { cn } from "@/lib/utils";
import { JsonPreview } from "../custom/jsonPreview";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { FRONTEND_INPUT_TOOL_NAMES, SUB_AGENT_TOOL_NAMES } from "@/lib/toolConstants";
import { useDebugMode } from "@/components/custom/contexts/userSettingsContext";
import { useMcpServers } from "@/components/custom/contexts/mcpServersContext";
import { McpServerIcon, hasMcpServerIcon } from "@/lib/mcpServerIcons";
import { useTranslation } from "react-i18next";
import { Spinner } from "@/components/ui/spinner";
import { RequestCommitApprovalStatusBadge } from "@/components/assistant-ui/tools/RequestCommitApprovalStatusBadge";
import { InlineApprovalCard } from "@/components/assistant-ui/InlineApprovalCard";

const REQUEST_COMMIT_APPROVAL_TOOL = "request_commit_approval";

// Represents the runtime shape of a content part from assistant-ui extended with
// our custom fields. Avoids `any` while covering both native (.status) and
// custom (.mcpServerName, .toolTitle, .argsText) properties.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ContentPartLike = Record<string, any> & {
  type: string;
  toolCallId?: string;
  toolName?: string;
  toolTitle?: string;
  argsText?: string;
  mcpServerName?: string;
  status?: { type: string };
  state?: string;
};

const EMPTY_PARTS: readonly ContentPartLike[] = [];

function getToolRangeSignature(
  parts: readonly ContentPartLike[],
  startIndex: number,
  endIndex: number,
): string {
  let signature = `${parts.length}:${startIndex}:${endIndex};`;
  for (let i = startIndex; i <= endIndex; i++) {
    const part = parts[i];
    if (!part) {
      signature += `${i}:missing;`;
      continue;
    }
    if (part.type !== "tool-call") {
      signature += `${i}:${part.type};`;
      continue;
    }
    signature += [
      i,
      part.toolCallId,
      part.toolName,
      part.toolTitle,
      part.mcpServerName,
      part.state,
      part.status?.type,
      part.progressMessage,
      part.argsText,
      typeof part.result === "string" ? part.result : part.result == null ? "" : "object-result",
    ].join(":");
    signature += ";";
  }
  return signature;
}

function isJsonString(str: string) {
  try {
    JSON.parse(str);
  } catch (e)
  {
    return false;
  }
  return true;
}

export const ToolFallback: ToolCallMessagePartComponent = ({
  toolCallId,
  toolName,
  argsText,
  result,
  status
}) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Sub-agent tools (research, continue_research) render via their sub-agent-lane;
  // suppress the generic ToolFallback pill so it never appears in the message body.
  if (toolName && SUB_AGENT_TOOL_NAMES.has(toolName)) return null;

  const isCancelled = status?.type === "incomplete" && status.reason === "cancelled";

  const isRunning = status?.type === "running";

  const cancelledReason =
    isCancelled && status.error
      ? typeof status.error === "string"
        ? status.error
        : JSON.stringify(status.error)
      : null;

  return (
    <div
      className={cn(
        "aui-tool-fallback-root flex w-full flex-col gap-3 rounded-lg py-1 text-xs",
        isCancelled && "border-muted-foreground/30 bg-muted/30",
      )}
    >
      <div className="aui-tool-fallback-header flex items-center gap-2 px-4">
        {isCancelled ? (
          <XCircleIcon className="aui-tool-fallback-icon size-3 text-muted-foreground" />
        ) :
        isRunning ?
        (
          <Spinner size="xs" className="aui-tool-fallback-icon text-muted-foreground" />
        ) :
        (
          <CheckIcon className="aui-tool-fallback-icon size-3" />
        )}
        <p
          className={cn(
            "aui-tool-fallback-title grow",
            isCancelled && "text-muted-foreground line-through",
          )}
        >
          {isCancelled ? t("tools.cancelled") : isRunning ? t("tools.running") : t("tools.completed")}
          <b>{toolName}</b>
        </p>
        <div className="cursor-pointer" onClick={() => setIsCollapsed(!isCollapsed)}>
          {isCollapsed ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </div>
      </div>
      {!isCollapsed && (
        <div className="aui-tool-fallback-content flex flex-col gap-2 border-t pt-2">
          {cancelledReason && (
            <div className="aui-tool-fallback-cancelled-root px-4">
              <p className="aui-tool-fallback-cancelled-header font-semibold text-muted-foreground">
                Cancelled reason:
              </p>
              <p className="aui-tool-fallback-cancelled-reason text-muted-foreground">
                {cancelledReason}
              </p>
            </div>
          )}
          <div
            className={cn(
              "aui-tool-fallback-args-root px-4",
              isCancelled && "opacity-60",
            )}
          >
            <JsonPreview value={argsText} />
          </div>
          {(!isCancelled && result) && (
            <div className="aui-tool-fallback-result-root border-t border-dashed px-4 pt-2">
              <p className="aui-tool-fallback-result-header font-semibold">
                Result:
              </p>
              {isJsonString(result) ? (
                <JsonPreview value={result} />
              ) : (
              <div className="aui-tool-fallback-result-text whitespace-pre-wrap text-sm text-muted-foreground border rounded bg-muted/50 px-2 py-1">
                {result}
              </div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TOOL_GROUP_ANIMATION_DURATION = 200;
const FALLBACK_SERVER = "__other__";



type ServerGroup = {
  serverName: string;
  runIndex: number;
  childIndices: number[];
  isRunning: boolean;
  toolNameCounts: Map<string, { count: number; title: string | undefined }>;
};

function buildServerGroups(
  parts: readonly ContentPartLike[],
  startIndex: number,
  endIndex: number,
): ServerGroup[] {
  const groupMap = new Map<string, ServerGroup>();
  const groups: ServerGroup[] = [];

  for (let i = startIndex; i <= endIndex; i++) {
    const part = parts[i];
    if (part?.type !== "tool-call") continue;

    const serverName = part.mcpServerName || FALLBACK_SERVER;
    const toolName = part.toolName ?? "tool";
    const toolTitle = part.toolTitle;
    const running = part.status?.type === "running" || part.state === "partial" || part.state === "pending";

    let group = groupMap.get(serverName);
    if (!group) {
      group = {
        serverName,
        runIndex: 0,
        childIndices: [],
        isRunning: false,
        toolNameCounts: new Map(),
      };
      groupMap.set(serverName, group);
      groups.push(group);
    }

    group.childIndices.push(i);
    if (running) group.isRunning = true;

    const entry = group.toolNameCounts.get(toolName);
    if (entry) {
      if (running) entry.count++;
    } else {
      group.toolNameCounts.set(toolName, { count: running ? 1 : 0, title: toolTitle });
    }
  }

  return groups;
}

type ToolCallInfo = {
  toolCallId: string;
  toolName: string;
  toolTitle: string | undefined;
  argsText: string | undefined;
  isRunning: boolean;
};

function getToolCallsOrdered(
  parts: readonly ContentPartLike[],
  group: ServerGroup,
): ToolCallInfo[] {
  return group.childIndices.map((idx) => {
    const part = parts[idx];
    return {
      toolCallId: part.toolCallId ?? `tool-${idx}`,
      toolName: part.toolName ?? "tool",
      toolTitle: part.toolTitle,
      argsText: part.argsText,
      isRunning:
        part.status?.type === "running" ||
        part.state === "partial" ||
        part.state === "pending",
    };
  });
}

function getActivityDescription(group: ServerGroup): string | null {
  if (!group.isRunning) return null;

  let maxName: string | null = null;
  let maxCount = 0;
  let maxTitle: string | undefined;

  for (const [name, entry] of group.toolNameCounts) {
    if (entry.count > maxCount) {
      maxCount = entry.count;
      maxName = name;
      maxTitle = entry.title;
    }
  }

  if (!maxName) return null;
  return maxTitle ?? maxName;
}

const SpinnerIcon: FC<{ className?: string }> = ({ className }) => (
  <Spinner size="xs" className={cn("text-muted-foreground", className)} />
);

const ToolGroupImpl: FC<PropsWithChildren<{ startIndex: number; endIndex: number }>> = ({
  children,
  startIndex,
  endIndex,
}) => {
  const { t } = useTranslation();
  const { debugMode } = useDebugMode();
  const { allServers } = useMcpServers();
  const stablePartsRef = useRef<readonly ContentPartLike[]>(EMPTY_PARTS);
  const partsSignatureRef = useRef("");

  const parts = useAuiState(({ message }) => {
    const nextParts = (message.parts ?? EMPTY_PARTS) as readonly ContentPartLike[];
    const nextSignature = getToolRangeSignature(nextParts, startIndex, endIndex);
    if (nextSignature !== partsSignatureRef.current) {
      partsSignatureRef.current = nextSignature;
      stablePartsRef.current = nextParts;
    }
    return stablePartsRef.current;
  });
  const isToolsRunning = useAuiState(({ message }) => {
    if (message.status?.type !== "running") return false;
    for (let i = startIndex; i <= endIndex; i++) {
      const part = message.parts[i];
      if (part?.type === "tool-call" && part.status?.type === "running") return true;
    }
    return false;
  });

  const serverGroups = useMemo(
    () => buildServerGroups(parts, startIndex, endIndex),
    [parts, startIndex, endIndex],
  );

  const serverConfigMap = useMemo(() => {
    const map = new Map<string, { heading?: string; runningText?: string; finishedText?: string }>();
    for (const s of allServers) {
      map.set(s.name, {
        heading: t(`${s.name}.toolGroupHeading`),
        runningText: t(`${s.name}.toolGroupRunningText`),
        finishedText: t(`${s.name}.toolGroupFinishedText`),
      });
    }
    return map;
  }, [allServers, t]);

  const childArray = useMemo(() => React.Children.toArray(children), [children]);

  // Split children: frontend-input tools render their own interactive card and
  // must always be visible; everything else is the MCP/native tool-call trace
  // that the collapsible "Used tools" wrapper hides unless debug mode is on.
  //
  // `request_commit_approval` is special: pending state is rendered by the sidebar
  // (`ApprovalActionsRow`, gated on `approvalStore`), NOT by an inline card here —
  // pushing the default `ToolFallback` row into `frontendNodes` would surface a
  // generic "Tool used: request_commit_approval" pill that has nothing useful in it
  // (Phase 1 BUG 2 fix). Completed state (Content carries the "approved …" / "rejected …"
  // sentinel) renders as a small `RequestCommitApprovalStatusBadge` so the audit trail
  // is visible in history without the generic pill.
  const { frontendInputNodes, debugGroups } = useMemo(() => {
    const frontendNodes: React.ReactNode[] = [];
    const debugOnlyGroups = serverGroups
      .map(g => ({
        ...g,
        childIndices: g.childIndices.filter(ci => {
          const part = parts[ci] as ContentPartLike;
          const toolName = part?.toolName;
          if (!toolName || !FRONTEND_INPUT_TOOL_NAMES.has(toolName)) return true;

          if (toolName === REQUEST_COMMIT_APPROVAL_TOOL) {
            const partState = part?.state;
            const isPending = partState === "pending" || partState === "partial" || part?.status?.type === "running";
            if (isPending) {
              // Render inline approval card in-thread instead of hiding.
              const toolCallId = part?.toolCallId as string | undefined;
              if (toolCallId) {
                frontendNodes.push(
                  <InlineApprovalCard
                    key={`approval-card-${toolCallId}`}
                    toolCallId={toolCallId}
                  />,
                );
              }
              return false;
            }
            // Completed: for approved case, hide badge — the document card on the message
            // is the visual signal. For rejected, render the status badge.
            const content = typeof part?.result === "string"
              ? part.result
              : typeof part?.content === "string"
                ? part.content
                : "";
            const isApproved = content.startsWith("approved tempFileId=");
            if (!isApproved && content) {
              frontendNodes.push(
                <RequestCommitApprovalStatusBadge
                  key={`approval-status-${part?.toolCallId ?? ci}`}
                  content={content}
                />,
              );
            }
            return false;
          }

          // Other frontend-input tools (ask / ask_open) keep today's behaviour:
          // push the default child node so their card or completed pill stays inline.
          frontendNodes.push(childArray[ci - startIndex]);
          return false;
        }),
      }))
      .filter(g => g.childIndices.length > 0);
    return { frontendInputNodes: frontendNodes, debugGroups: debugOnlyGroups };
  }, [serverGroups, parts, childArray, startIndex]);

  const frontendCards = frontendInputNodes.length > 0 ? (
    <div className="aui-frontend-input-tools flex w-full flex-col gap-2">
      {frontendInputNodes}
    </div>
  ) : null;

  if (!debugMode) return frontendCards;

  if (debugGroups.length === 0) return frontendCards;

  return (
    <>
      {frontendCards}
    <Collapsible
      defaultOpen={true}
      className="aui-tool-group-root w-full py-0.5"
      style={{ "--animation-duration": `${TOOL_GROUP_ANIMATION_DURATION}ms` } as React.CSSProperties}
    >
      <CollapsibleTrigger
        className={cn(
          "aui-tool-group-trigger group/trigger flex max-w-[75%] items-center gap-1.5 py-0.5 text-muted-foreground text-xs transition-colors hover:text-foreground ps-6",
          isToolsRunning && "animate-pulse",
        )}
      >
        {debugMode && <>
        <span className="font-medium">
          {isToolsRunning ? t("tools.usingTools") : t("tools.usedTools")}
        </span>
        <ChevronDownIcon
          className={cn(
            "mt-0.5 size-3 shrink-0",
            "transition-transform duration-200 ease-out",
            "group-data-[state=closed]/trigger:-rotate-90",
            "group-data-[state=open]/trigger:rotate-0",
          )}
        />
        </>}
      </CollapsibleTrigger>
      <CollapsibleContent
        className={cn(
          "overflow-hidden pl-4",
          "data-[state=closed]:animate-collapsible-up",
          "data-[state=open]:animate-collapsible-down",
          "data-[state=closed]:fill-mode-forwards",
          "data-[state=open]:duration-[var(--animation-duration)]",
          "data-[state=closed]:duration-[var(--animation-duration)]",
        )}
      >
        {debugGroups.map((group) => {
          const config = serverConfigMap.get(group.serverName);
          const headingKey = `${group.serverName}.toolGroupHeading`;
          const heading = config?.heading ?? (group.serverName === FALLBACK_SERVER ? t("tools.otherTools") : (t(headingKey) !== headingKey ? t(headingKey) : group.serverName));
          const runningKey = `${group.serverName}.toolGroupRunningText`;
          const runningText = config?.runningText ?? (t(runningKey) !== runningKey ? t(runningKey) : undefined);
          const finishedKey = `${group.serverName}.toolGroupFinishedText`;
          const finishedText = config?.finishedText ?? (t(finishedKey) !== finishedKey ? t(finishedKey) : undefined);
          const showIcon = hasMcpServerIcon(group.serverName);
          const activity = getActivityDescription(group);

          if (debugMode) {
            return (
              <div key={`${group.serverName}-${group.runIndex}`} className="mb-2">
                <div className="flex items-center gap-2 py-1 ps-2">
                  {showIcon && (
                    <McpServerIcon name={group.serverName} className="size-4 rounded-sm object-contain shrink-0" />
                  )}
                  <span className={cn("text-xs font-semibold", group.isRunning && "animate-pulse")}>
                    {group.isRunning ? (runningText ?? heading) : (finishedText ?? heading)}
                  </span>
                  {group.isRunning && <SpinnerIcon />}
                </div>
                <div className="pl-2">
                  {group.childIndices.map((ci) => childArray[ci - startIndex])}
                </div>
              </div>
            );
          }

          // Clean view (debug off)
          return (
            <div key={`${group.serverName}-${group.runIndex}`} className="mb-1.5">
              <div className="flex items-center gap-2 py-1 ps-2">
                {showIcon && (
                  <McpServerIcon name={group.serverName} className="size-4 rounded-sm object-contain shrink-0" />
                )}
                {group.isRunning ? (
                  <SpinnerIcon />
                ) : (
                  <CheckIcon className="size-3 text-muted-foreground" />
                )}
                <span className={cn("text-xs font-medium", group.isRunning && "animate-pulse")}>
                  {group.isRunning ? (runningText ?? heading) : (finishedText ?? heading)}
                </span>
              </div>
              {group.isRunning && activity && (
                <p className="ps-10 text-xs text-muted-foreground animate-pulse">
                  {activity}
                </p>
              )}
            </div>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
    </>
  );
};

export const ToolGroup = memo(ToolGroupImpl);
ToolGroup.displayName = "ToolGroup";
